// Build information utility
// This will be populated at build time with actual git info

const BUILD_INFO = {
  version: process.env.REACT_APP_VERSION || 'auto',
  buildTime: process.env.REACT_APP_BUILD_TIME || new Date().toISOString(),
  gitCommit: process.env.REACT_APP_GIT_COMMIT || 'dev',
  gitBranch: process.env.REACT_APP_GIT_BRANCH || 'local',
  environment: process.env.NODE_ENV || 'development',
  deployEnv: process.env.REACT_APP_DEPLOY_ENV || 'local'
};

// Auto-generate version from build time if not explicitly set
const getAutoVersion = () => {
  if (BUILD_INFO.version !== 'auto') {
    return BUILD_INFO.version;
  }
  
  // Generate version from build timestamp
  // Format: YYYY.MM.DD.build (e.g., 2024.01.15.1234)
  const buildDate = new Date(BUILD_INFO.buildTime);
  const year = buildDate.getFullYear();
  const month = String(buildDate.getMonth() + 1).padStart(2, '0');
  const day = String(buildDate.getDate()).padStart(2, '0');
  
  // Build number from time (HHMM format)
  const hours = String(buildDate.getHours()).padStart(2, '0');
  const minutes = String(buildDate.getMinutes()).padStart(2, '0');
  const buildNum = `${hours}${minutes}`;
  
  return `${year}.${month}.${day}.${buildNum}`;
};

// Get short commit hash (first 7 characters)
const getShortCommit = () => {
  return BUILD_INFO.gitCommit.substring(0, 7);
};

// Determine environment tag based on deployment context
const getEnvironmentTag = () => {
  // 1. Check explicit deploy environment variable (set during CI/CD)
  if (BUILD_INFO.deployEnv === 'production' || BUILD_INFO.deployEnv === 'prod') {
    return ''; // No tag for production
  }
  
  if (BUILD_INFO.deployEnv === 'staging' || BUILD_INFO.deployEnv === 'stg') {
    return '-stg';
  }
  
  // 2. Check current URL/hostname for cloud deployments
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // Production domains (no tag)
    if (hostname === 'app.lps.solutions' || 
        hostname === 'lps.solutions' ||
        (hostname.endsWith('.lps.solutions') && !hostname.includes('dev') && !hostname.includes('stg'))) {
      return '';
    }
    
    // Staging domains
    if (hostname.includes('stg') || 
        hostname.includes('staging') ||
        hostname === 'app-stg.lps.solutions') {
      return '-stg';
    }
    
    // Development domains
    if (hostname.includes('dev') || 
        hostname.includes('development') ||
        hostname === 'app-dev.lps.solutions' ||
        hostname === 'localhost') {
      return '-dev';
    }
  }

  // 3. Fallback to git branch detection
  if (BUILD_INFO.gitBranch === 'main' || BUILD_INFO.gitBranch === 'master') {
    return ''; // Production - no tag
  }
  
  if (BUILD_INFO.gitBranch === 'staging' || BUILD_INFO.gitBranch.includes('stg')) {
    return '-stg';
  }
  
  // 4. Default to dev for development/unknown environments
  return '-dev';
};

// Get formatted version string
export const getVersionString = () => {
  const version = getAutoVersion();
  const envTag = getEnvironmentTag();
  
  // For auto-generated versions, don't include git commit (already timestamp-based)
  if (BUILD_INFO.version === 'auto') {
    return `v${version}${envTag}`;
  }
  
  // For explicit versions, include git commit if available
  if (BUILD_INFO.gitCommit === 'dev' || BUILD_INFO.gitCommit === 'unknown') {
    return `v${version}${envTag}`;
  }
  
  return `v${version}${envTag}-${getShortCommit()}`;
};

// Get full build info
export const getBuildInfo = () => {
  return {
    ...BUILD_INFO,
    shortCommit: getShortCommit(),
    formattedVersion: getVersionString()
  };
};

export default BUILD_INFO;